import { CheckOutlined, SearchOutlined, ShopOutlined, UpOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Dropdown, Empty, Spin, Tag, Tooltip, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { listPharmacies } from '@/api/pharmacies.api';
import { useAuth } from '@/auth/auth-context';
import { usePharmacyScope } from '@/auth/pharmacy-scope';
import { queryKeys } from '@/lib/query-keys';
import { useDebounced } from '@/lib/use-debounced';

const { Text } = Typography;

/**
 * The pharmacy that Listings, Stock and Supply rates are showing.
 *
 * Built rather than an antd `Select`, for two reasons. A Select is a form
 * control — it looks like something you are filling in, when this is the
 * context everything below it is filtered by, closer to a workspace switcher
 * than to a field. And its trigger can only render a string, so the one thing
 * worth seeing without opening it — which shop, in which city, and whether it
 * can actually trade — had nowhere to go.
 *
 * Unscoped is a real state and says so, because landing on an empty Listings
 * screen and not knowing why is the failure this control exists to prevent.
 *
 * Collapsed, it narrows to the avatar rather than unmounting. It used to vanish
 * with the sider, which left the scope filtering three screens with nothing on
 * the page admitting that it was — the same failure, arrived at from the other
 * direction.
 */
export function PharmacyScopePicker({ collapsed }: { collapsed?: boolean }) {
  const { can } = useAuth();
  const { pharmacyId, pharmacyName, setPharmacy, assigned } = usePharmacyScope();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const debounced = useDebounced(term, 250);

  const query = useMemo(() => ({ q: debounced || undefined, limit: 25 }), [debounced]);

  const { data, isFetching } = useQuery({
    queryKey: queryKeys.pharmacies.list(query),
    queryFn: () => listPharmacies(query),
    enabled: can('pharmacy:read') && open,
    staleTime: 60_000,
  });

  const rows = data?.data ?? [];

  if (!can('pharmacy:read')) return null;

  const menu = (
    <div className="scope-panel">
      <div className="scope-search">
        {/* Bare, like the picker's own search box: the panel is already a
            bordered surface, and a second border inside it reads as a field
            dropped into a menu. */}
        <SearchOutlined className="scope-search-icon" />
        <input
          autoFocus
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Search pharmacies"
          className="select-search-control"
          aria-label="Search pharmacies"
        />
      </div>

      <div className="scope-list">
        {isFetching && rows.length === 0 ? (
          <div className="flex justify-center py-6">
            <Spin size="small" />
          </div>
        ) : rows.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No matching pharmacy"
            className="my-4!"
          />
        ) : (
          rows.map((pharmacy) => {
            const current = pharmacy.id === pharmacyId;
            return (
              <button
                key={pharmacy.id}
                type="button"
                className={`scope-option ${current ? 'scope-option-current' : ''}`}
                onClick={() => {
                  setPharmacy(pharmacy.id, pharmacy.name);
                  setOpen(false);
                  setTerm('');
                }}
              >
                <span className="scope-avatar">{pharmacy.name.charAt(0).toUpperCase()}</span>
                <span className="min-w-0 flex-1">
                  <span className="scope-option-name">{pharmacy.name}</span>
                  <Text type="secondary" className="block truncate text-[11px]">
                    {pharmacy.city} · {pharmacy.pincode}
                  </Text>
                </span>
                {/* A shop with no verified licence cannot open or take orders. */}
                {pharmacy.hasVerifiedLicense ? null : (
                  <Tag color="error" className="m-0! text-[10px]!">
                    unverified
                  </Tag>
                )}
                {assigned.includes(pharmacy.id) ? (
                  <Tag className="m-0! text-[10px]!">yours</Tag>
                ) : null}
                {current ? <CheckOutlined className="text-[11px] opacity-70" /> : null}
              </button>
            );
          })
        )}
      </div>

      <div className="scope-footer">
        {pharmacyId ? (
          <button
            type="button"
            className="scope-clear"
            onClick={() => {
              setPharmacy(null, null);
              setOpen(false);
            }}
          >
            Clear selection
          </button>
        ) : (
          <span />
        )}
        <Link to="/pharmacies" onClick={() => setOpen(false)} className="scope-manage">
          Manage
        </Link>
      </div>
    </div>
  );

  const label = pharmacyName ?? 'No pharmacy chosen';

  const trigger = (
    <button
      type="button"
      className={[
        'scope-trigger',
        pharmacyId ? '' : 'scope-trigger-empty',
        collapsed ? 'scope-trigger-collapsed' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-expanded={open}
      aria-label={`Pharmacy in scope: ${label}. Choose the pharmacy to work on`}
    >
      {pharmacyId ? (
        <span className="scope-avatar">{(pharmacyName ?? '?').charAt(0).toUpperCase()}</span>
      ) : (
        <span className="scope-avatar scope-avatar-empty">
          <ShopOutlined />
        </span>
      )}
      {collapsed ? null : (
        <>
          <span className="min-w-0 flex-1 text-left">
            <span className="scope-trigger-label">{label}</span>
            <Text type="secondary" className="block truncate text-[10.5px]">
              {pharmacyId ? 'Working on' : 'Stock and listings need one'}
            </Text>
          </span>
          <UpOutlined className="scope-caret" />
        </>
      )}
    </button>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      placement="bottomLeft"
      popupRender={() => menu}
    >
      {/* Collapsed, the name has nowhere to sit, so it moves to a tooltip. */}
      {collapsed ? <Tooltip title={label} placement="right">{trigger}</Tooltip> : trigger}
    </Dropdown>
  );
}
